/**
 * The expansion's counting kinds (10 §3): several objects to work, several targets to mine,
 * several zones to stand in, one zone to hold. Each keeps `done / total` honest, and gives up
 * the moment what is left can no longer add up to what the mission needs.
 */

import type { Ctx } from '../ctx';
import { OBJECTIVES, secs } from '../tuning';
import type { MissionDef, PropDef, Rect } from '../types';
import {
  completeObjective, failObjective, inRect, neededCount, propDestroyed, propDistance, targetProps,
} from './common';
import { wearsUniform } from './disguise';

/** Ticks of hold-still work at each `useObjects` target; 0 = Interact does it at once. */
export function useActionTicks(def: MissionDef): number {
  return secs(Math.max(0, def.objective.holdSeconds ?? 0));
}

/** A blackout switch not yet thrown is worth working whatever became of the objective: it still darkens its lamps. */
function liveSwitch(ctx: Pick<Ctx, 'world' | 'statics'>, propId: number): boolean {
  const groups = ctx.statics.def.blackoutGroups ?? [];
  return groups.some((g, i) => g.switchPropId === propId && ctx.world.objective.blackouts[i] !== true);
}

/**
 * The nearest target that still wants working and is within Interact's reach, or null. Once the objective has
 * FAILED (the plans were burned) there is nothing left to take: no Interact is offered, and the prompt goes.
 */
export function usableTarget(ctx: Pick<Ctx, 'world' | 'statics'>): PropDef | null {
  const { world: w, statics } = ctx;
  if (statics.def.objective.kind !== 'useObjects') return null;
  let best: PropDef | null = null;
  let bestD: number = OBJECTIVES.useReach;
  for (const prop of targetProps(statics.def)) {
    if (w.objective.usedPropIds.includes(prop.id) || propDestroyed(w, prop.id)) continue;
    if (w.objective.failed && !liveSwitch(ctx, prop.id)) continue;
    const d = propDistance(prop, w.ranger.x, w.ranger.z);
    if (d <= bestD) {
      bestD = d;
      best = prop;
    }
  }
  return best;
}

export function stepUseObjects(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const targets = targetProps(statics.def);
  o.done = o.usedPropIds.length;
  const needed = neededCount(statics.def, targets.length);
  if (o.done >= needed) {
    completeObjective(ctx);
    return;
  }
  // What has been blown up can no longer be worked (the code book burns with the post: 03 §2, mission 2).
  const left = targets.filter((t) => !o.usedPropIds.includes(t.id) && !propDestroyed(w, t.id)).length;
  if (o.done + left < needed) failObjective(ctx, 'DESTROYED BEFORE IT COULD BE USED');
}

export function stepPlantAtTargets(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const targets = targetProps(statics.def);
  for (const target of targets) {
    if (o.plantedPropIds.includes(target.id) || propDestroyed(w, target.id)) continue;
    if (w.bombs.some((b) => propDistance(target, b.x, b.z) <= OBJECTIVES.plantRadius)) o.plantedPropIds.push(target.id);
  }
  o.done = o.plantedPropIds.length;
  const needed = neededCount(statics.def, targets.length);
  if (o.done >= needed) {
    completeObjective(ctx);
    return;
  }
  const left = targets.filter((t) => !o.plantedPropIds.includes(t.id) && !propDestroyed(w, t.id)).length;
  if (o.done + left < needed) failObjective(ctx, 'THE TARGET WENT UP TOO SOON');
}

/** Was he seen this tick? (`dwellUnseen`: a sighting throws the count away. A motion detector reports to nobody.) */
function seenNow(ctx: Ctx): boolean {
  return ctx.events.some((ev) => {
    if (ev.type !== 'spotted') return false;
    const spotter = ctx.world.enemies.find((e) => e.id === ev.id);
    return !spotter || wearsUniform(spotter.kind);
  });
}

/** Does time in a zone count this tick? He must be up and in it, and the window - if there is one - open. */
function counting(ctx: Ctx, zone: Rect): boolean {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  if (r.status !== 'active' || !inRect(zone, r.x, r.z)) return false;
  return statics.def.objective.duringWindow !== true || w.objective.windowOpen;
}

export function stepVisitZones(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const zones = statics.def.objective.zones ?? [];
  const dwell = secs(Math.max(0, statics.def.objective.dwellSeconds ?? 0));
  const seen = statics.def.objective.dwellUnseen === true && seenNow(ctx);
  zones.forEach((zone, i) => {
    if (o.zonesDone[i] === true) return;
    // Leaving, or being seen at it, starts that point again from nothing.
    o.zoneTicks[i] = counting(ctx, zone) && !seen ? (o.zoneTicks[i] ?? 0) + 1 : 0;
    if ((o.zoneTicks[i] ?? 0) > 0 && (o.zoneTicks[i] ?? 0) >= dwell) o.zonesDone[i] = true;
  });
  o.done = o.zonesDone.filter((d) => d).length;
  if (o.done >= neededCount(statics.def, zones.length)) completeObjective(ctx);
}

export function stepHoldZone(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const zone = statics.def.objective.zone;
  if (!zone || o.complete) return;
  // Outside the zone the clock only pauses; seen while `dwellUnseen`, it starts again.
  if (statics.def.objective.dwellUnseen === true && inRect(zone, w.ranger.x, w.ranger.z) && seenNow(ctx)) o.holdTicks = 0;
  else if (counting(ctx, zone)) o.holdTicks++;
  if (o.holdTicks >= secs(statics.def.objective.dwellSeconds ?? 0)) {
    o.done = 1;
    completeObjective(ctx);
  }
}
